import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * BlueprintBackdrop — ambient "imprinted on the page beneath glass" engineering
 * blueprint that drifts behind a section content.
 *
 * - Renders an oversized image (120% scale) absolutely positioned behind content.
 * - z-index 0; section content sits at z-10.
 * - Hue-shifted into our cool indigo/cyan palette (no orange/teal hand-paper feel).
 * - Vignette-masked at the edges → never a hard rectangle.
 * - Slow scroll-based parallax (Y translate) + imperceptible rotation drift.
 * - Mobile: opacity halved, no rotation, smaller parallax.
 *
 * Required: parent must be `position: relative` and `overflow: hidden`.
 */
export default function BlueprintBackdrop({
    src,
    opacity = 0.07,
    parallaxRange = 120,
    rotateRange = 1.5,    // degrees from start to end of section
    hueShift = true,
    eager = false,        // load immediately if true; otherwise lazy
}) {
    const ref = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        const mqMobile = window.matchMedia("(max-width: 767px)");
        const mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        const sync = () => {
            setIsMobile(mqMobile.matches);
            setReducedMotion(mqMotion.matches);
        };
        sync();
        mqMobile.addEventListener?.("change", sync);
        mqMotion.addEventListener?.("change", sync);
        return () => {
            mqMobile.removeEventListener?.("change", sync);
            mqMotion.removeEventListener?.("change", sync);
        };
    }, []);

    const effectiveOpacity = isMobile ? opacity * 0.55 : opacity;
    const effectiveParallax = isMobile ? Math.min(parallaxRange, 40) : parallaxRange;
    const effectiveRotate = isMobile || reducedMotion ? 0 : rotateRange;

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    // Parallax: image moves slower than scroll (negative range to start above-center)
    const y = useTransform(
        scrollYProgress,
        [0, 1],
        [-effectiveParallax / 2, effectiveParallax / 2]
    );
    const rotate = useTransform(
        scrollYProgress,
        [0, 1],
        [-effectiveRotate / 2, effectiveRotate / 2]
    );
    // Subtle entrance opacity ramp (avoid sudden pop)
    const o = useTransform(
        scrollYProgress,
        [0, 0.18, 0.82, 1],
        [0, effectiveOpacity, effectiveOpacity, 0]
    );

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none overflow-hidden"
            style={{
                // Vignette mask: fade to transparent at edges
                WebkitMaskImage:
                    "radial-gradient(ellipse 95% 80% at 50% 50%, #000 35%, rgba(0,0,0,0.7) 65%, transparent 100%)",
                maskImage:
                    "radial-gradient(ellipse 95% 80% at 50% 50%, #000 35%, rgba(0,0,0,0.7) 65%, transparent 100%)",
            }}
        >
            <motion.img
                src={src}
                alt=""
                loading={eager ? "eager" : "lazy"}
                decoding="async"
                draggable={false}
                style={{
                    position: "absolute",
                    top: "-10%",
                    left: "-10%",
                    width: "120%",
                    height: "120%",
                    objectFit: "cover",
                    objectPosition: "center",
                    y,
                    rotate,
                    opacity: o,
                    // Cool shift into indigo/cyan palette + heavy desaturation + darken
                    filter: hueShift
                        ? "hue-rotate(170deg) saturate(0.55) brightness(0.55) contrast(1.05) blur(0.4px)"
                        : "saturate(0.6) brightness(0.55) contrast(1.05) blur(0.4px)",
                    // Multiply against the deep bg keeps it ink-on-paper feel without going light
                    mixBlendMode: "screen",
                    willChange: "transform, opacity",
                }}
            />
            {/* Cool tint wash — pulls any leftover warmth into the brand palette */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(0,219,255,0.04) 0%, transparent 35%, rgba(255,107,255,0.022) 100%)",
                    mixBlendMode: "screen",
                }}
            />
        </div>
    );
}
